"use client";

import React from "react";
import { Calendar, MapPin, FileText, CreditCard, Map as MapIcon, ArrowLeft, CircleAlert, Users } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";


declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleTournoiConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="238083" className="container mx-auto px-4 py-12 animate-pulse">
        <div className="h-6 w-48 bg-muted rounded-lg mb-8"></div>
        <div className="h-12 w-3/4 bg-muted rounded-lg mb-4"></div>
        <div className="flex gap-4 mb-12">
          <div className="h-6 w-32 bg-muted rounded-lg"></div>
          <div className="h-6 w-32 bg-muted rounded-lg"></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 h-96 bg-muted rounded-2xl"></div>
          <div className="h-64 bg-muted rounded-2xl"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12">
        <Alert variant="destructive" className="rounded-2xl border-destructive/50">
          <CircleAlert className="h-4 w-4" />
          <AlertTitle className="font-default">Erreur</AlertTitle>
          <AlertDescription className="font-default">
            Impossible de charger les détails du tournoi. Veuillez réessayer plus tard.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h2 className="text-2xl font-default font-bold text-foreground mb-4">Tournoi introuvable</h2>
        <p className="text-muted-foreground mb-8 font-default">Le tournoi que vous recherchez n'existe pas ou a été supprimé.</p>
        <Link to="/TournamentsPage">
          <Button variant="outline" className="rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour aux tournois
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const cf = post.customFields || {};

  
  let formattedDate = cf.date;
  if (cf.date) {
    try {
      const dateObj = new Date(cf.date);
      if (!isNaN(dateObj.getTime())) {
        formattedDate = new Intl.DateTimeFormat('fr-FR', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }).format(dateObj);
      }
    } catch (e) {
      
    }
  }

  const tournamentName = cf.name || post.title;

  return (
    <div className="container mx-auto px-4 py-8 md:py-12">
      
      <div className="mb-8">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/HomePage" className="text-muted-foreground hover:text-primary transition-colors">Accueil</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/TournamentsPage" className="text-muted-foreground hover:text-primary transition-colors">Tournois</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-default font-semibold text-primary">{tournamentName}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      
      <div className="mb-12">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-default font-bold text-foreground tracking-tighter mb-6 uppercase italic">
              {tournamentName}
            </h1>
            
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 text-muted-foreground">
              {formattedDate && (
                <div className="flex items-center gap-2">
                  <div className="bg-primary/20 p-2 rounded-full">
                    <Calendar className="h-5 w-5 text-primary" />
                  </div>
                  <span className="font-default font-semibold text-foreground capitalize">{formattedDate}</span>
                </div>
              )}
              
              {cf.location && (
                <div className="flex items-center gap-2">
                  <div className="bg-primary/20 p-2 rounded-full">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <span className="font-default font-semibold text-foreground">{cf.location}</span>
                </div>
              )}

              {cf.max_players && (
                <div className="flex items-center gap-2">
                  <div className="bg-primary/20 p-2 rounded-full">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <span className="font-default font-semibold text-foreground">
                    Nombre de joueurs maximum : {cf.max_players}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="shrink-0">
            <Link to="/TournamentsPage">
              <Button variant="outline" className="hidden md:flex rounded-full border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground transition-all">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour aux tournois
              </Button>
            </Link>
          </div>
        </div>
      </div>

      
      {post.featuredImage && (
        <div className="mb-12 rounded-3xl overflow-hidden shadow-2xl border border-primary/10 h-[300px] md:h-[400px] lg:h-[500px] relative">
          <img 
            src={post.featuredImage} 
            alt={tournamentName} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-80"></div>
        </div>
      )}

      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        
        <div className="lg:col-span-2 space-y-8">
          <Card className="border-border/40 shadow-xl overflow-hidden rounded-3xl bg-card/30 backdrop-blur-md">
            <div className="h-1.5 w-full bg-gradient-to-r from-primary via-primary/50 to-primary"></div>
            <CardHeader>
              <CardTitle className="text-3xl font-default font-bold uppercase tracking-tighter italic text-foreground">Détails de l'Événement</CardTitle>
            </CardHeader>
            <CardContent>
              {cf.description ? (
                <div className="prose prose-neutral dark:prose-invert max-w-none whitespace-pre-wrap text-muted-foreground leading-relaxed font-default text-lg">
                  {cf.description}
                </div>
              ) : post.content ? (
                <div 
                  className="prose prose-neutral dark:prose-invert max-w-none text-muted-foreground leading-relaxed font-default text-lg"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              ) : (
                <p className="text-muted-foreground italic font-default">Aucune description disponible pour ce tournoi prestigieux.</p>
              )}
            </CardContent>
          </Card>
        </div>

        
        <div className="space-y-6">
          <Card className="border-primary/30 shadow-2xl bg-secondary/50 backdrop-blur-xl rounded-3xl">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-default font-bold uppercase italic text-primary">Inscriptions & Infos</CardTitle>
              <CardDescription className="font-default text-muted-foreground">Rejoignez l'élite du football jeune</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              
              {cf.payment_link && (
                <Link to={cf.payment_link} newTab className="w-full block">
                  <Button size="lg" className="w-full font-default font-bold text-lg h-16 shadow-lg hover:shadow-primary/20 transition-all rounded-full bg-primary text-primary-foreground hover:scale-[1.02]">
                    <CreditCard className="mr-2 h-6 w-6" />
                    S'inscrire Maintenant
                  </Button>
                </Link>
              )}

              {cf.pdf_regulation && (
                <Link to={cf.pdf_regulation} newTab className="w-full block">
                  <Button variant="outline" className="w-full justify-start h-14 border-primary/20 hover:bg-primary/10 hover:text-primary rounded-2xl font-default transition-colors">
                    <FileText className="mr-3 h-5 w-5 text-primary" />
                    Règlement Officiel (PDF)
                  </Button>
                </Link>
              )}

              {cf.map_link && (
                <Link to={cf.map_link} newTab className="w-full block">
                  <Button variant="outline" className="w-full justify-start h-14 border-primary/20 hover:bg-primary/10 hover:text-primary rounded-2xl font-default transition-colors">
                    <MapIcon className="mr-3 h-5 w-5 text-primary" />
                    Localisation du Stade
                  </Button>
                </Link>
              )}

              {(!cf.payment_link && !cf.pdf_regulation && !cf.map_link) && (
                <div className="text-sm text-muted-foreground text-center py-6 bg-muted/20 rounded-2xl font-default border border-dashed border-border">
                  Les modalités d'inscription et documents techniques seront publiés prochainement.
                </div>
              )}
            </CardContent>
          </Card>

          
          <div className="md:hidden pt-4">
            <Link to="/TournamentsPage" className="w-full block">
              <Button variant="ghost" className="w-full font-default text-muted-foreground hover:text-primary">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour aux tournois
              </Button>
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}

export default function SingleTournoiTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "tournoi" }; 
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-tournoi-template-section" className="bg-background text-foreground min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <SingleTournoiConsumer />
      </PostsProvider>
    </section>
  );
}